import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clients as clientsApi, projects as projectsApi, aiApi, settings as settingsApi } from '@/utils/api';
import {
  Building2, Mail, Phone, Globe, ArrowLeft, Plus, Sparkles,
  FolderOpen, AlertCircle, RefreshCw, ExternalLink, X, CircleDot
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'New': '#5C6B72', 'Scoping': '#006080', 'Estimate Sent': '#D9A441',
  'Approved': '#3C8A5A', 'In Progress': '#006080', 'UAT': '#0066CC',
  'Delivered': '#3C8A5A', 'Archived': '#8A99A0',
};

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [brief, setBrief] = useState('');
  const [briefUpdated, setBriefUpdated] = useState('');
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const briefRef = useRef(null);
  const [projectForm, setProjectForm] = useState({
    name: '', description: '', project_type: '', status: 'New',
    budget_band: '', timeline: '', planned_delivery_date: '',
  });

  useEffect(() => {
    Promise.all([
      clientsApi.get(clientId),
      projectsApi.list({ client_id: clientId }),
      aiApi.latestBrief(clientId),
      settingsApi.projectTypes(),
    ]).then(([c, p, b, pt]) => {
      setClient(c.data);
      setProjects(p.data);
      setBrief(b.data.brief || '');
      setBriefUpdated(b.data.updated_at || '');
      setProjectTypes(pt.data);
    }).catch(() => {});
  }, [clientId]);

  const generateBrief = async () => {
    setGeneratingBrief(true);
    setBrief('');
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${apiBase}/ai/brief/${clientId}`, {
        method: 'POST', credentials: 'include',
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                accumulated += data.text;
                setBrief(accumulated);
              }
              if (data.error) toast.error(data.error);
            } catch {}
          }
        }
      }
    } catch (err) {
      toast.error('Failed to generate brief. Check Claude API key in Settings.');
    } finally {
      setGeneratingBrief(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const r = await projectsApi.create({
        ...projectForm,
        client_id: clientId,
        brand_id: client?.brand_id || '',
      });
      setProjects(prev => [r.data, ...prev]);
      setShowCreateProject(false);
      toast.success('Project created');
      navigate(`/admin/clients/${clientId}/projects/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    }
  };

  if (!client) return (
    <div className="p-6">
      <div className="h-8 w-48 rounded animate-pulse mb-4" style={{ background: 'var(--border)' }} />
      <div className="h-32 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      {/* Back button */}
      <button
        className="flex items-center gap-1.5 text-sm mb-4 transition-colors hover:opacity-70"
        style={{ color: 'var(--muted)' }}
        onClick={() => navigate('/admin/clients')}
        data-testid="back-to-clients"
      >
        <ArrowLeft size={14} /> Back to clients
      </button>

      {/* Client header */}
      <div className="card-base p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-card flex items-center justify-center text-lg font-black text-white flex-shrink-0"
              style={{ background: client.brand_color || '#006080' }}
            >
              {client.company_name?.charAt(0)}
            </div>
            <div>
              <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>{client.company_name}</h1>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>{client.contact_name}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {client.email && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                    <Mail size={12} />{client.email}
                  </span>
                )}
                {client.phone && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                    <Phone size={12} />{client.phone}
                  </span>
                )}
                {client.website && (
                  <a href={client.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs" style={{ color: '#006080' }}>
                    <Globe size={12} />{client.website}
                  </a>
                )}
              </div>
            </div>
          </div>
          {client.brand_name && (
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{ background: `${client.brand_color || '#006080'}15`, color: client.brand_color || '#006080' }}
            >
              {client.brand_name}
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Left — projects */}
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>
              Projects ({projects.length})
            </h2>
            <button
              className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5"
              onClick={() => setShowCreateProject(true)}
              data-testid="create-project-button"
            >
              <Plus size={13} /> New Project
            </button>
          </div>

          {projects.length === 0 ? (
            <div className="card-base p-8 text-center">
              <FolderOpen size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No projects yet</p>
              <button className="mt-3 text-xs font-semibold" style={{ color: '#006080' }} onClick={() => setShowCreateProject(true)}>
                Create first project →
              </button>
            </div>
          ) : (
            projects.map(p => (
              <div
                key={p.id}
                className="card-interactive p-4"
                onClick={() => navigate(`/admin/clients/${clientId}/projects/${p.id}`)}
                data-testid={`project-item-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5">
                    <CircleDot size={14} style={{ color: STATUS_COLORS[p.status] || '#006080', marginTop: 2 }} />
                    <div>
                      <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{p.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{p.project_type}</p>
                    </div>
                  </div>
                  <span
                    className="badge-status text-xs"
                    style={{
                      background: `${STATUS_COLORS[p.status] || '#006080'}15`,
                      color: STATUS_COLORS[p.status] || '#006080'
                    }}
                  >
                    {p.status}
                  </span>
                </div>
                {p.description && (
                  <p className="text-xs mt-2 line-clamp-2" style={{ color: 'var(--muted)' }}>{p.description}</p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Right — AI brief */}
        <div className="lg:col-span-2">
          <div className="card-base p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={15} style={{ color: '#006080' }} />
                <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>AI Brief</h2>
              </div>
              <button
                className="flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-btn transition-colors"
                style={{ color: '#006080', background: '#E6F1F4' }}
                onClick={generateBrief}
                disabled={generatingBrief}
                data-testid="generate-brief-button"
              >
                <RefreshCw size={12} className={generatingBrief ? 'animate-spin' : ''} />
                {generatingBrief ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {briefUpdated && (
              <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
                Updated {new Date(briefUpdated).toLocaleDateString()}
              </p>
            )}

            {brief ? (
              <div
                className="text-xs leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto"
                style={{ color: 'var(--ink)' }}
                ref={briefRef}
                data-testid="ai-brief-content"
              >
                {brief}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Sparkles size={24} style={{ color: 'var(--border)', margin: '0 auto 8px' }} />
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  {generatingBrief ? 'Generating brief with Claude...' : 'Click Generate to create an AI-powered brief'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create project modal */}
      {showCreateProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-md p-5 animate-fade-in" data-testid="create-project-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black" style={{ color: 'var(--ink)' }}>New Project</h2>
              <button onClick={() => setShowCreateProject(false)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateProject} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Project Name *</label>
                <input className="input-agency text-sm" required value={projectForm.name} onChange={e => setProjectForm({...projectForm, name: e.target.value})} data-testid="project-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Project Type *</label>
                <select className="input-agency text-sm" required value={projectForm.project_type} onChange={e => setProjectForm({...projectForm, project_type: e.target.value})} data-testid="project-type-select">
                  <option value="">Select type...</option>
                  {projectTypes.map(pt => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</label>
                <textarea className="input-agency text-sm resize-none" rows={3} value={projectForm.description} onChange={e => setProjectForm({...projectForm, description: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Budget Band</label>
                  <input className="input-agency text-sm" placeholder="e.g. $5k-$10k" value={projectForm.budget_band} onChange={e => setProjectForm({...projectForm, budget_band: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Timeline</label>
                  <input className="input-agency text-sm" placeholder="e.g. 6 weeks" value={projectForm.timeline} onChange={e => setProjectForm({...projectForm, timeline: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Planned delivery date</label>
                <input className="input-agency text-sm" type="date" value={projectForm.planned_delivery_date} onChange={e => setProjectForm({...projectForm, planned_delivery_date: e.target.value})} data-testid="project-planned-delivery-date" />
                <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Surfaces on the Delivery Calendar.</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreateProject(false)}>Cancel</button>
                <button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="create-project-submit">Create Project</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
